import React, {useContext, useEffect, useState} from 'react';
import {actHistoryGetAPI, HistoryFilterParams} from "../http/actionHistoryAPI";
import {Context} from "../index";
import {usersGetApi} from "../http/userApi";
import {compHistoryGetAPI} from "../http/companyHistoryAPI";
import {companyGetAPI} from "../http/companyAPI";
import '../styles/HistoryContainer.css';
import {WsTriggers} from "../WebsocketTriggers";
import {observer} from "mobx-react-lite";
import { suppHistoryGetAPI } from '../http/supplierHistory';


const PAGE_SIZE = 20;
const SCROLL_THRESHOLD = 80;

const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
};

type ActHistType = {
    id: number;
    context: string;
    createdAt?: string;
    updatedAt: string;
    userId: number;
    date?: string;
    time?: string;
    userName?: string;
}

type FiltersType = {
    userId: string;
    companyId: string;
    dateFrom: string;
    dateTo: string;
}

const emptyFilters: FiltersType = { userId: '', companyId: '', dateFrom: '', dateTo: '' };

const HistoryContainer = observer((props: {
    historyType: 'actionHistory' | 'companyHistory' | 'supplierHistory',
    containerName: string,
}) => {
    const containerName = props.containerName
    const { user, ws } = useContext(Context)

    const REQUESTS = {
        'actionHistory': (params: HistoryFilterParams) => actHistoryGetAPI(params),
        'companyHistory': (params: HistoryFilterParams) =>
            compHistoryGetAPI(window.location.pathname.split('/').pop(), params),
        'supplierHistory': (params: HistoryFilterParams) =>
            suppHistoryGetAPI(window.location.pathname.split('/').pop(), params),

    }

    const [filters, setFilters] = useState<FiltersType>(emptyFilters);
    const [items, setItems] = useState<Array<ActHistType>>([]);
    const [hasMore, setHasMore] = useState<boolean>(false);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

    const [userDict, setUserDict] = useState<{[key: number]: string} | null>(null);
    const [companyList, setCompanyList] = useState<Array<{id: number, name: string}> | null>(null);

    const formatItem = (raw: any): ActHistType => {
        // @ts-ignore
        const date = new Date(raw.createdAt).toLocaleDateString('ru-RU', options).split(' ');
        return {
            id: raw.id,
            context: raw.context,
            updatedAt: raw.updatedAt,
            userId: raw.userId,
            date: `${date[0]} ${date[1]} ${date[2]}`,
            time: `${date[5]}`,
        };
    }

    const requestHistoryData = (reset: boolean) => {
        const offset = reset ? 0 : items.length;
        if (!reset) setIsLoadingMore(true);

        const params: HistoryFilterParams = {
            limit: PAGE_SIZE,
            offset,
            userId: filters.userId || undefined,
            companyId: filters.companyId || undefined,
            dateFrom: filters.dateFrom || undefined,
            dateTo: filters.dateTo || undefined,
        };

        REQUESTS[props.historyType](params).then((response) => {
            if (response.data) {
                const newItems = (response.data.items || []).map(formatItem);
                setItems(reset ? newItems : (prev) => [...prev, ...newItems]);
                setHasMore(!!response.data.hasMore);
            }
        }).catch((err) => {
            console.log(err)
        }).finally(() => {
            setIsLoadingMore(false);
        })
    }

    useEffect(() => {
        const message = ws.getMsg;
        if (message !== null) {
            if (message === WsTriggers.HISTORY) {
                requestHistoryData(true);
                ws.setMessage(null);
            }
        }
    }, [ws.getMsg]);


    useEffect(() => {
        usersGetApi()
            .then((response) => {
                if (response.data) {
                    const usersObj: {[key: number]: string} = {};
                    response.data.forEach((el: {id: number, username: string}) => {
                        usersObj[el.id] = el.username
                    })
                    setUserDict(usersObj);
                }
            })
            .catch((err) => {
                console.log(err)
            })

        if (props.historyType === 'actionHistory') {
            companyGetAPI()
                .then((response) => {
                    if (response.data && response.data.companies) {
                        setCompanyList(response.data.companies.map((c: any) => ({ id: c.id, name: c.name })));
                    }
                })
                .catch((err) => {
                    console.log(err)
                })
        }
    }, []);

    useEffect(() => {
        requestHistoryData(true)
    }, [filters]);

    const handleFilterChange = (field: keyof FiltersType, value: string) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
    }

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_THRESHOLD;
        if (nearBottom && hasMore && !isLoadingMore) {
            requestHistoryData(false);
        }
    }

    return (
        <div className={`${containerName}__history-container ${containerName}__data-container contentWindow`}>
            <div className={`${containerName}__data-header contentWindow__header`}><span
                className={'contentWindow__name'}>История</span></div>
            <div className={`historyContainer__filters`}>
                <select className={'select historyContainer__filter'}
                        value={filters.userId}
                        onChange={(e) => handleFilterChange('userId', e.target.value)}>
                    <option value={''}>Все пользователи</option>
                    {
                        userDict ? Object.keys(userDict).map((id) =>
                            <option key={`histUserOpt-${id}`} value={id}>{userDict[Number(id)]}</option>
                        ) : null
                    }
                </select>
                {
                    props.historyType === 'actionHistory' ?
                        <select className={'select historyContainer__filter'}
                                value={filters.companyId}
                                onChange={(e) => handleFilterChange('companyId', e.target.value)}>
                            <option value={''}>Все предприятия</option>
                            {
                                companyList ? companyList.map((c) =>
                                    <option key={`histCompOpt-${c.id}`} value={c.id}>{c.name}</option>
                                ) : null
                            }
                        </select>
                        :
                        null
                }
                <input className={'input historyContainer__filter'} type={'date'}
                       value={filters.dateFrom}
                       onChange={(e) => handleFilterChange('dateFrom', e.target.value)}/>
                <input className={'input historyContainer__filter'} type={'date'}
                       value={filters.dateTo}
                       onChange={(e) => handleFilterChange('dateTo', e.target.value)}/>
            </div>
            <div id='histBody' onScroll={handleScroll}
                 className={`historyContainer__data-body historyContainer__data-history-body contentWindow__body`}>
                {
                    items.length > 0 ?
                        items.map((el, i) =>
                            <React.Fragment key={`histItm${el.id}`}>
                                {
                                    i === 0 || items[i - 1].date !== el.date ?
                                        <div className={`historyContainer__history-item-date-container`}>
                                            <span className={`historyContainer__history-date`}>{el.date}</span>
                                        </div>
                                        :
                                        null
                                }
                                <div className={`historyContainer__history-item`}>
                                    <div className={`historyContainer__history-item-time-container`}>
                                        <span>{el.time}</span>
                                        <span>{userDict ? userDict[el.userId] : null}</span>
                                    </div>
                                    <div className={`historyContainer__history-item-context-container`}
                                         dangerouslySetInnerHTML={{__html: el.context}}/>
                                </div>
                            </React.Fragment>
                        )
                        :
                        <div className={`historyContainer__history-empty`}>Записей не найдено</div>
                }
                {
                    isLoadingMore ?
                        <div className={`historyContainer__history-loading`}>Загрузка...</div>
                        :
                        null
                }
            </div>
        </div>
    );
});

export default HistoryContainer;
